import css from 'styled-jsx/css';
import colors from '../../../../../config/styles/colors';
import { svgToDataUri } from '../../../../../utils/styles/svgMixins';
import { videoIcon } from '../../../ImageGallery.style';

const { gray, white, secondary_color: secondaryColor } = colors;

export const ImageGalleryCarouselV2Styles = (theme) => {
  const { color: themeColor = secondaryColor } = theme || {};

  return css`
    /* Block: Main carousel component */
    .image-gallery-carousel {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100%;
      position: relative;
    }

    /* Element: Container for carousel items */
    .image-gallery-carousel__container {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: center;
      text-align: center;
      margin: 0;
      width: 100%;
      gap: 8px;
      overflow-x: hidden;
      overflow-y: hidden;
      scrollbar-width: none;
      -ms-overflow-style: none;
      min-height: 420px;
      transition: all 0.3s ease-in-out;
    }

    .image-gallery-carousel__container::-webkit-scrollbar {
      display: none;
    }

    /* Element: Individual carousel item */
    .image-gallery-carousel__item {
      outline: none;
      width: 104.5px;
      height: auto;
      position: relative;
      cursor: pointer;
      overflow: hidden;
      transition: all 0.2s ease-in-out;
      border: 1px solid #f1f1f1;
      aspect-ratio: 1/1;
    }

    /* Element Modifier: Video carousel item */
    .image-gallery-carousel__item--video {
      background: url('${svgToDataUri(videoIcon)}');
      background-size: 48px 48px;
      background-clip: border-box;
      background-repeat: no-repeat;
      background-position: center center;
      background-color: ${gray.shade10};
    }

    /* Element: Video placeholder inside item */
    .image-gallery-carousel__item-video {
      height: 100%;
      width: 100%;
      position: relative;
      z-index: 1;
      overflow: visible;
    }

    /* Element Modifier: Video with thumbnail background */
    .image-gallery-carousel__item-video--with-thumbnail {
      background: transparent;
    }

    /* Element: Video with thumbnail - image positioning */
    .image-gallery-carousel__item-video--with-thumbnail :global(img) {
      position: relative;
      z-index: 1;
    }

    /* Element: Carousel item image styling */
    .image-gallery-carousel__item :global(img) {
      height: 100%;
      width: 100%;
      object-fit: cover;
      position: relative;
      z-index: 1;
      transition: transform 0.2s ease;
    }

    /* Block Modifier: Rebranded theme support */
    .image-gallery-carousel--rebranded .image-gallery-carousel__item--active {
      border: 1px solid ${themeColor};
      box-shadow: 0 0 0 1px ${themeColor}20;
    }

    /* Element: Indicators container - Hidden in new design */
    .image-gallery-carousel__indicators {
      display: none;
    }

    /* Element: Individual indicator dot - Hidden in new design */
    .image-gallery-carousel__indicator {
      display: none;
    }

    /* Element Modifier: Active indicator - Hidden in new design */
    .image-gallery-carousel__indicator--active {
      display: none;
    }

    /* Block Modifier: Rebranded indicator styling - Hidden in new design */
    .image-gallery-carousel--rebranded .image-gallery-carousel__indicator--active {
      display: none;
    }

    /* Hover states for better UX - Scale only the image, not the container */
    .image-gallery-carousel__item:hover :global(img) {
      transform: scale(1.1);
    }

    .image-gallery-carousel__item:hover:not(.image-gallery-carousel__item--active) {
      border-color: ${gray.shade20};
    }

    /* Focus states for accessibility */
    .image-gallery-carousel__item:focus {
      outline: none;
      box-shadow: 0 0 0 2px ${themeColor}, 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    /* Element: Navigation arrows */
    .image-gallery-carousel__nav-arrow {
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      width: 32px;
      height: 32px;
      border: 1px solid ${gray.shade20};
      border-radius: 50%;
      background-color: ${white.shade1};
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 8;
      transition: all 0.2s ease-in-out;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    .image-gallery-carousel__nav-arrow:hover {
      border-color: ${themeColor};
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      transform: translateX(-50%) scale(1.05);
    }

    .image-gallery-carousel__nav-arrow:focus {
      outline: none;
      box-shadow: 0 0 0 2px ${themeColor}, 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    /* Element Modifier: Up arrow positioning */
    .image-gallery-carousel__nav-arrow--up {
      top: -16px;
    }

    /* Element Modifier: Down arrow positioning */
    .image-gallery-carousel__nav-arrow--down {
      bottom: -16px;
    }

    /* Element: Arrow icons */
    .image-gallery-carousel__nav-arrow .icon {
      font-size: 16px;
      color: ${gray.shade60};
      transition: color 0.2s ease-in-out;
    }

    /* Hover state for icons */
    .image-gallery-carousel__nav-arrow:hover .icon {
      color: ${themeColor};
    }

    /* Focus state for icons */
    .image-gallery-carousel__nav-arrow:focus .icon {
      color: ${themeColor};
    }

    /* Responsive design */
    @media (max-width: 768px) {
      .image-gallery-carousel__container {
        max-width: 100%;
        gap: 6px;
        padding: 0 6px;
        max-height: 324px;
        min-height: 324px;
      }

      .image-gallery-carousel__item {
        height: 60px;
        width: 60px;
        min-width: 60px;
      }

      .image-gallery-carousel__nav-arrow {
        width: 28px;
        height: 28px;
      }

      .image-gallery-carousel__nav-arrow--up {
        top: -14px;
      }

      .image-gallery-carousel__nav-arrow--down {
        bottom: -14px;
      }

      .image-gallery-carousel__nav-arrow .icon {
        font-size: 14px;
      }
    }

    @media (max-width: 480px) {
      .image-gallery-carousel__container {
        gap: 4px;
        max-height: 266px;
        min-height: 266px;
      }

      .image-gallery-carousel__item {
        height: 50px;
        width: 50px;
        min-width: 50px;
      }

      .image-gallery-carousel__nav-arrow {
        width: 24px;
        height: 24px;
      }

      .image-gallery-carousel__nav-arrow--up {
        top: -12px;
      }

      .image-gallery-carousel__nav-arrow--down {
        bottom: -12px;
      }

      .image-gallery-carousel__nav-arrow .icon {
        font-size: 12px;
      }
    }
  `;
};
